import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  MoreHorizontal, 
  Calendar, 
  CheckSquare, 
  MessageSquare,
  View,
  Edit,
  Copy,
  Archive,
  Trash2
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  "Planning": { bg: "bg-purple-100", text: "text-purple-700" },
  "Active": { bg: "bg-green-100", text: "text-green-700" },
  "On Hold": { bg: "bg-amber-100", text: "text-amber-700" },
  "In Review": { bg: "bg-blue-100", text: "text-blue-700" },
  "Completed": { bg: "bg-teal-100", text: "text-teal-700" },
  "Archived": { bg: "bg-gray-100", text: "text-gray-700" }
};

const PRIORITY_CONFIG = {
  "High": { bg: "bg-red-100", text: "text-red-700", icon: "🔴" },
  "Medium": { bg: "bg-amber-100", text: "text-amber-700", icon: "🟡" },
  "Low": { bg: "bg-green-100", text: "text-green-700", icon: "🟢" }
};

const ProjectCard = ({ project, onEdit, onDelete, onDuplicate, onArchive }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG["Planning"];
  const priorityConfig = PRIORITY_CONFIG[project.priority] || PRIORITY_CONFIG["Medium"];
  
  const getProgressColor = (progress) => {
    if (progress < 33) return "bg-red-500";
    if (progress < 66) return "bg-amber-500";
    return "bg-green-500";
  };

  const isOverdue = project.dueDate && new Date(project.dueDate) < new Date() && project.status !== "Completed";
  
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('[data-menu]')) {
      return;
    }
    navigate(`/projects/${project.id}`);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (index) => {
    const colors = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-pink-500 to-pink-600",
      "from-green-500 to-green-600",
      "from-amber-500 to-amber-600",
      "from-red-500 to-red-600"
    ];
    return colors[index % colors.length];
  };

  const assignedMembers = project.assignedMembers || [];
  const displayedMembers = assignedMembers.slice(0, 4);
  const remainingCount = assignedMembers.length - 4;

  const menuItems = [
    { icon: View, label: "View Details", action: () => navigate(`/projects/${project.id}`) },
    { icon: Edit, label: "Edit Project", action: () => onEdit && onEdit(project) },
    { icon: Copy, label: "Duplicate", action: () => onDuplicate && onDuplicate(project) },
    { icon: Archive, label: "Archive", action: () => onArchive && onArchive(project) },
    { icon: Trash2, label: "Delete", action: () => onDelete && onDelete(project), danger: true }
  ];

  return (
    <Card 
      className={cn(
        "overflow-hidden cursor-pointer transition-all duration-300",
        isHovered ? "shadow-lg -translate-y-1" : "shadow-sm hover:shadow-md"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      <div 
        className="h-2 transition-all duration-300"
        style={{ backgroundColor: project.color || "#6366F1" }}
      />
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <h3 className="font-bold text-navy text-lg line-clamp-1">{project.name}</h3>
          <div className="relative" data-menu>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
                {menuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      item.action();
                      setShowMenu(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-50",
                      item.danger ? "text-red-600" : "text-navy"
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <Badge className={cn("w-fit mt-1", statusConfig.bg, statusConfig.text)}>
          {project.status}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {project.description && (
          <p className="text-sm text-gray-500 line-clamp-2">{project.description}</p>
        )}

        <div className="flex items-center gap-2">
          <span className={cn("text-sm", priorityConfig.text)}>
            {priorityConfig.icon} {project.priority || "Medium"} Priority
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Progress</span>
            <span className="font-medium text-navy">{project.progress || 0}%</span>
          </div>
          <Progress 
            value={project.progress || 0} 
            className="h-2"
            indicatorClassName={getProgressColor(project.progress || 0)}
          />
        </div>

        {assignedMembers.length > 0 && (
          <div className="flex items-center -space-x-2">
            {displayedMembers.map((memberId, index) => (
              <div
                key={memberId}
                className={cn(
                  "w-8 h-8 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-medium border-2 border-white",
                  getAvatarColor(index)
                )}
                title="Team member"
              >
                {getInitials(`User ${index + 1}`)}
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-medium border-2 border-white">
                +{remainingCount}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <CheckSquare className="w-4 h-4" />
              {project.tasksCount || 0}/{project.totalTasks || 20} Tasks
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {project.commentsCount || 0}
            </span>
          </div>
          {project.dueDate && (
            <span className={cn("flex items-center gap-1", isOverdue && "text-red-600")}>
              <Calendar className="w-4 h-4" />
              {formatDate(project.dueDate)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;